import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getCaseStudies, getLogoRoster, getSiteCopy } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { ArrowRight } from "lucide-react";
import { amp } from "@/lib/amp";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const Dot = () => (
  <span className="text-[color:var(--copper)] px-1.5" aria-hidden="true">&middot;</span>
);

export default function Work() {
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });
  const { data: cases = [] } = useQuery({ queryKey: ["case-studies"], queryFn: getCaseStudies });
  const { data: roster = [] } = useQuery({ queryKey: ["logo-roster"], queryFn: getLogoRoster });

  const [filter, setFilter] = useState("All");

  // Filter chips: derived from distinct type values across published records.
  const types = useMemo(() => {
    const seen = [];
    cases.forEach((c) => { if (c.type && !seen.includes(c.type)) seen.push(c.type); });
    return ["All", ...seen];
  }, [cases]);

  const filtered = filter === "All" ? cases : cases.filter((c) => c.type === filter);

  // Group case studies by sector (c.type), preserving first-seen order.
  const groupedBySector = useMemo(() => {
    const order = [];
    const map = {};
    filtered.forEach((c) => {
      const sector = c.type || "Other";
      if (!map[sector]) { map[sector] = []; order.push(sector); }
      map[sector].push(c);
    });
    return order.map((sector) => ({ sector, items: map[sector] }));
  }, [filtered]);

  return (
    <div className="exec-scope min-h-screen" data-testid="work-page">
      <Seo {...PAGE_SEO.work} jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Work", path: "/work" }])} />
      <section className="exec-container pt-24 md:pt-28 pb-10">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">The work</p>
          <h1 className="font-extralight text-[clamp(34px,4.8vw,60px)] leading-[1.1] mt-6">
            Real engagements. Real{" "}
            <span className="exec-serif italic font-medium text-[color:var(--copper)]">outcomes.</span>
          </h1>
          <p data-testid="work-intro" className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6">
            {copy.work_intro}
          </p>
        </div>
      </section>

      {/* Filter row */}
      <section className="exec-container">
        <div data-testid="work-filter-row" className="flex flex-wrap gap-2.5 pb-10">
          {types.map((t) => {
            const active = filter === t;
            return (
              <button
                key={t}
                data-testid={`work-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => { setFilter(t); track("case_filter", { type: t }); }}
                className={`text-[11px] uppercase tracking-[0.18em] font-normal px-4 py-2 rounded-[2px] border transition-colors duration-300 ${
                  active
                    ? "border-[color:var(--copper)] text-[color:var(--copper-deep)] bg-[color:var(--copper)]/[0.07]"
                    : "border-[color:var(--rule)] text-[color:var(--grey)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
                }`}
                aria-pressed={active}
              >
                {amp(t)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Case grid — grouped by sector */}
      <section className="exec-container pb-4" data-testid="work-case-grid">
        {groupedBySector.map(({ sector, items }) => (
          <div key={sector} className="mb-14 last:mb-2" data-testid="work-sector-group">
            <div className="flex items-center gap-4 mb-6">
              <p className="exec-eyebrow whitespace-nowrap" data-testid="work-sector-label">{amp(sector)}</p>
              <span className="h-px flex-1 bg-[color:var(--rule)]" aria-hidden="true" />
              <span className="text-[11px] tracking-[0.14em] uppercase text-[color:var(--grey)] font-normal">
                {items.length} {items.length === 1 ? "case" : "cases"}
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              {items.map((c) => (
                <CaseCard key={c.slug} c={c} />
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="exec-serif text-[17px] text-[color:var(--grey)]">No cases in this category yet.</p>
        )}
      </section>

      {/* Roster band */}
      <section className="exec-container py-20">
        <div className="exec-rule" />
        <div data-testid="home-client-roster" className="py-12">
          <h2 className="text-[12px] uppercase tracking-[0.2em] text-center font-normal text-[color:var(--ink)]">{copy.roster_title}</h2>
          <div className="mt-10 space-y-10">
            {roster.map((g) => (
              <div key={g.group}>
                <p className="exec-eyebrow text-center">{g.group}</p>
                <div className="mt-4 flex flex-wrap justify-center gap-x-9 gap-y-3 text-[13px] tracking-[0.14em] font-light text-[color:var(--ink)]">
                  {g.names.map((n) => (
                    <span key={n} className="uppercase">{n}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="exec-rule" />
      </section>
    </div>
  );
}

function CaseCard({ c }) {
  const to = `/work/${c.slug}`;
  const detailSegments = [
    c.sector_label ? `Sector: ${c.sector_label}` : null,
    c.the_work ? `The work: ${c.the_work}` : null,
    c.timeframe ? c.timeframe : null,
  ].filter(Boolean);

  return (
    <motion.article
      {...fade}
      data-testid="work-case-study-card"
      data-type={c.type}
      className="exec-card flex flex-col"
    >
      <div className="p-8">
        {/* Sector tag */}
        <p className="exec-eyebrow" data-testid="case-sector-tag">{amp(c.type)}</p>

        {/* Headline (links to the full case page) */}
        <h3
          data-testid="case-headline"
          className="font-light text-[clamp(20px,2.4vw,27px)] leading-[1.18] mt-4"
        >
          <Link
            to={to}
            onClick={() => track("case_study_view", { type: c.type, slug: c.slug })}
            className="transition-colors duration-200 hover:text-[color:var(--copper-deep)]"
          >
            {c.headline || c.pull_line}
          </Link>
        </h3>

        {/* Reframe: Problem / Caliber / Outcome (falls back to body for legacy data) */}
        {(c.card_problem && c.caliber && c.card_outcome) ? (
          <div className="mt-5 space-y-4" data-testid="case-reframe">
            <ReframeBlock label="Problem" text={c.card_problem} />
            <ReframeBlock label="Caliber" text={c.caliber} />
            <ReframeBlock label="Outcome" text={c.card_outcome} />
          </div>
        ) : (
          (c.body || c.pull_line) && (
            <p data-testid="case-body" className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">
              {c.body || c.pull_line}
            </p>
          )
        )}

        {/* Detail line */}
        {detailSegments.length > 0 && (
          <p
            data-testid="case-detail-line"
            className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--grey)] font-normal mt-5 flex flex-wrap items-center"
          >
            {detailSegments.map((seg, i) => (
              <span key={i} className="inline-flex items-center">
                {seg}
                {i < detailSegments.length - 1 && <Dot />}
              </span>
            ))}
          </p>
        )}
      </div>

      <div className="px-8 pb-7 mt-auto">
        <Link
          to={to}
          data-testid="case-read-link"
          onClick={() => track("case_study_view", { type: c.type, slug: c.slug })}
          className="exec-link"
        >
          Read the case <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </motion.article>
  );
}

function ReframeBlock({ label, text }) {
  if (!text) return null;
  return (
    <div data-testid={`case-reframe-${label.toLowerCase()}`}>
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--copper-deep)] font-normal">{label}</p>
      <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-1.5">{text}</p>
    </div>
  );
}
